import { useEffect, useState } from "react";
import { motion, useMotionValue, useTransform, animate } from "framer-motion";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { useDialogs } from "./DialogsContext";
import { ArrowRight, Sparkles } from "lucide-react";

function AnimatedNumber({ value, prefix = "", suffix = "" }) {
  const mv = useMotionValue(0);
  const rounded = useTransform(mv, (v) =>
    `${prefix}${Math.round(v).toLocaleString()}${suffix}`
  );
  useEffect(() => {
    const controls = animate(mv, value, {
      duration: 0.9,
      ease: [0.22, 1, 0.36, 1],
    });
    return controls.stop;
  }, [value, mv]);
  return <motion.span>{rounded}</motion.span>;
}

const CURRENCIES = [
  { code: "USD", symbol: "$" },
  { code: "GBP", symbol: "£" },
  { code: "EUR", symbol: "€" },
];

export default function RevenueCalculator() {
  const [dbSize, setDbSize] = useState(5000);
  const [aov, setAov] = useState(450);
  const [rate, setRate] = useState(3);
  const [email, setEmail] = useState("");
  const [currency, setCurrency] = useState("USD");

  const symbol = CURRENCIES.find((c) => c.code === currency)?.symbol || "$";
  const recoverable = dbSize * aov * (rate / 100);
  const monthly = Math.round(recoverable / 6); // assume 6-month campaign window

  const { openBook } = useDialogs();

  return (
    <section
      id="calculator"
      data-testid="revenue-calculator"
      className="relative py-24 lg:py-32 border-b border-white/10 bg-[#070707]"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.25em] text-[#ccff00] font-semibold mb-4 flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" />
              / Revenue Rescue Calculator
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase leading-[0.95]">
              How much
              <br />
              money is
              <br />
              <span className="text-[#ccff00]">sleeping</span>
              <br />
              in your CRM?
            </h2>
            <p className="mt-6 text-white/60 max-w-md leading-relaxed">
              Drag the sliders. See your conservative recoverable revenue —
              based on the same benchmarks we use to qualify our own clients.
            </p>

            <div className="hidden lg:block mt-10 pt-8 border-t border-white/10">
              <div className="text-xs uppercase tracking-widest text-white/40 mb-3">
                Benchmarks we use
              </div>
              <ul className="space-y-2 text-sm text-white/60">
                <li className="flex gap-3"><span className="text-[#ccff00]">→</span> 2–4% reactivation rate is industry conservative.</li>
                <li className="flex gap-3"><span className="text-[#ccff00]">→</span> Most dormant contacts haven’t been touched in 90+ days.</li>
                <li className="flex gap-3"><span className="text-[#ccff00]">→</span> We project a 6-month campaign window.</li>
              </ul>
            </div>
          </div>

          {/* Calculator Panel */}
          <div className="lg:col-span-7 relative">
            <div className="absolute -inset-px bg-gradient-to-br from-[#ccff00]/40 via-transparent to-transparent pointer-events-none" />
            <div className="relative bg-[#0a0a0a] border border-white/10 p-6 lg:p-10">
              <div className="flex items-center justify-between mb-8 pb-6 border-b border-white/10">
                <span className="text-xs uppercase tracking-[0.18em] text-white/60 font-semibold">
                  Currency
                </span>
                <div className="flex border border-white/10">
                  {CURRENCIES.map((c) => (
                    <button
                      key={c.code}
                      type="button"
                      data-testid={`calculator-currency-${c.code}`}
                      onClick={() => setCurrency(c.code)}
                      className={`h-9 w-14 text-sm font-mono-acc border-r border-white/10 last:border-r-0 transition-colors ${
                        currency === c.code
                          ? "bg-[#ccff00] text-black font-bold"
                          : "text-white/70 hover:bg-white/5"
                      }`}
                    >
                      {c.symbol} {c.code}
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-10">
                <SliderControl
                  testid="calculator-db-size-slider"
                  label="Database size"
                  hint="Total contacts you’ve ever collected"
                  value={dbSize}
                  setValue={setDbSize}
                  min={500}
                  max={100000}
                  step={500}
                  format={(v) => `${v.toLocaleString()} contacts`}
                />
                <SliderControl
                  testid="calculator-aov-slider"
                  label="Avg. customer value"
                  hint="What one customer is worth to you"
                  value={aov}
                  setValue={setAov}
                  min={50}
                  max={10000}
                  step={50}
                  format={(v) => `${symbol}${v.toLocaleString()}`}
                />
                <SliderControl
                  testid="calculator-rate-slider"
                  label="Reactivation rate"
                  hint="% of dormant contacts we’ll recover (3% is conservative)"
                  value={rate}
                  setValue={setRate}
                  min={1}
                  max={8}
                  step={0.5}
                  format={(v) => `${v}%`}
                />
              </div>

              <div className="mt-10 pt-8 border-t border-white/10">
                <div className="text-xs uppercase tracking-widest text-white/40 mb-3">
                  Estimated recoverable revenue
                </div>
                <div
                  data-testid="calculator-result-metric"
                  className="font-display text-5xl sm:text-6xl lg:text-7xl text-[#ccff00] tabular-nums"
                >
                  <AnimatedNumber value={recoverable} prefix={symbol} />
                </div>
                <div className="mt-6">
                  <div className="border border-white/10 p-4">
                    <div className="text-[10px] uppercase tracking-widest text-white/40">
                      Approx. per month
                    </div>
                    <div className="font-display text-2xl mt-1 tabular-nums">
                      <AnimatedNumber value={monthly} prefix={symbol} />
                    </div>
                  </div>
                </div>

                <div className="mt-8 flex flex-col sm:flex-row gap-3">
                  <Input
                    data-testid="calculator-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="h-12 bg-[#050505] border-white/15 text-white placeholder:text-white/30 rounded-none focus-visible:ring-[#ccff00] focus-visible:ring-1"
                  />
                  <button
                    data-testid="calculator-book-cta"
                    onClick={openBook}
                    className="h-12 px-6 inline-flex items-center justify-center gap-2 bg-[#ccff00] text-black font-bold uppercase tracking-wider text-xs hover:bg-[#b3e600] transition-colors whitespace-nowrap"
                  >
                    Claim my audit
                    <ArrowRight className="w-4 h-4" strokeWidth={3} />
                  </button>
                </div>
                <p className="mt-3 text-xs text-white/40">
                  We’ll send a tailored revenue plan within 24 hours. No spam.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function SliderControl({ testid, label, hint, value, setValue, min, max, step, format }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-2">
        <label className="text-xs uppercase tracking-[0.18em] text-white/60 font-semibold">
          {label}
        </label>
        <span className="font-mono-acc text-sm text-[#ccff00] tabular-nums">
          {format(value)}
        </span>
      </div>
      <Slider
        data-testid={testid}
        value={[value]}
        onValueChange={(v) => setValue(v[0])}
        min={min}
        max={max}
        step={step}
        className="my-3"
      />
      <p className="text-xs text-white/40">{hint}</p>
    </div>
  );
}
